import Echo from 'laravel-echo'
import io from 'socket.io-client'
import type { SocketIoPrivateChannel } from 'laravel-echo/dist/channel'
import type { BroadcastingService } from '@/types/services/broadcasting/BroadcastingService.ts'
import type { EventListener, Whisper } from '@/types/services/broadcasting/BroadcastingEvents.ts'
// Activate debugging by setting localStorage.echoDebug to '1'
const debugLog = (message: string) => {
  if (localStorage?.broadcastDebug !== '1') return
  console.log(`[EchoService] ${message}`)
}

export class EchoService implements BroadcastingService {
  private static instance: EchoService
  private echo: Echo<'socket.io'> | null = null
  private listeners: EventListener[] = []

  protected constructor() {
    debugLog('Stopping Echo Instance on beforeunload event')
    window.addEventListener('beforeunload', () => this.stop())
  }

  public static getInstance(): EchoService {
    if (!EchoService.instance) {
      EchoService.instance = new EchoService()
    }
    return EchoService.instance
  }

  public start(authToken: string) {
    debugLog(`Starting Echo Instance with token: ${authToken}`)
    if (this.echo) {
      debugLog('Stopping existing Echo Instance before starting a new one')
      this.stop()
    }

    this.echo = new Echo({
      broadcaster: 'socket.io',
      host: import.meta.env.VITE_BROADCAST_URL,
      client: io,
      auth: {
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      },
    })
  }

  public stop() {
    debugLog('Stopping Echo Instance')
    if (!this.echo) {
      debugLog('No Echo instance to stop')
      return
    }
    this.echo.disconnect()
    this.echo = null
  }

  public restart(token: string) {
    const prevListeners = [...this.listeners]

    debugLog(`Restarting Echo Instance with token: ${token} and re-binding listeners`)

    this.stop()
    this.start(token)

    // addEventListener registers each listener again: reset the registry so it doesn't grow with every restart
    this.listeners = []

    // Rebind event listeners
    prevListeners.forEach((listener) => {
      debugLog(
        `Rebinding listener for event: ${listener.event.event} on channel: ${listener.channelName} with identifier: ${listener.identifier}`,
      )
      this.addEventListener(listener)
    })
  }

  public addEventListener(listener: EventListener) {
    if (!this.echo) {
      debugLog('Echo instance not initialized, cannot add listener')
      return
    }
    this.listeners.push(listener)
    this.echo.private(`${listener.channelName}.${listener.identifier}`).listen(listener.event.event, listener.event.callback)

    debugLog(
      `Added listener for event: ${listener.event.event} on channel: ${listener.channelName} with identifier: ${listener.identifier}`,
    )
  }

  public removeEventListener(listener: EventListener) {
    // Compare by socket channel name: identifiers may be passed as number or string ('5' and 5 are the same channel)
    const toChannelName = (l: EventListener) => `${l.channelName}.${l.identifier}`
    const channelName = toChannelName(listener)
    const isSameListener = (l: EventListener) =>
      l.handlerName === listener.handlerName &&
      l.event.event === listener.event.event &&
      toChannelName(l) === channelName

    const registeredListeners = this.listeners.filter(isSameListener)
    // Prune the registry even while stopped, otherwise restart() would rebind already removed listeners
    this.listeners = this.listeners.filter((l) => !isSameListener(l))

    if (!registeredListeners.length) {
      // Don't call echo.private(): it would subscribe (and authenticate) the channel just to unbind nothing
      debugLog(`No registered listener for event: ${listener.event.event} on channel: ${channelName}, nothing to remove`)
      return
    }

    if (!this.echo) {
      debugLog(`Echo instance not initialized, removed ${registeredListeners.length} listener(s) from registry only`)
      return
    }

    // Unbind the callbacks that were actually registered: handlers rebuild their listener objects, and
    // unbinding by event name alone would also drop other handlers' callbacks for the same event.
    const channel = this.echo.private(channelName)
    registeredListeners.forEach((registered) => channel.stopListening(registered.event.event, registered.event.callback))

    debugLog(`Removed ${registeredListeners.length} listener(s) for event: ${listener.event.event} on channel: ${channelName}`)
  }

  public doWhisper(whisper: Whisper) {
    if (!this.echo) {
      debugLog('Echo instance not initialized, cannot send whisper')
      return
    }
    const channel = this.echo.private(`${whisper.channelName}.${whisper.identifier}`) as SocketIoPrivateChannel
    channel.whisper('typing', whisper.payload)

    debugLog(`Sent whisper on channel: ${whisper.channelName} with identifier: ${whisper.identifier}`)
  }
}
